import React, { useEffect, useRef, useState } from 'react';

// Shared singleton IntersectionObserver for all RevealOnScroll elements
// This avoids creating dozens of observers and Framer Motion GPU layers on mobile.
let sharedObserver = null;
const callbacks = new WeakMap();

function getSharedObserver() {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
        return null;
    }
    if (!sharedObserver) {
        sharedObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const cb = callbacks.get(entry.target);
                        if (cb) {
                            cb();
                            callbacks.delete(entry.target);
                        }
                        sharedObserver.unobserve(entry.target);
                    }
                });
            },
            {
                rootMargin: '0px 0px -40px 0px',
                threshold: 0.1
            }
        );
    }
    return sharedObserver;
}

const RevealOnScroll = ({ children, className = '', delay = 0, as: Component = 'div' }) => {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const observer = getSharedObserver();
        if (!observer) {
            setIsVisible(true);
            return;
        }

        callbacks.set(el, () => setIsVisible(true));
        observer.observe(el);

        return () => {
            callbacks.delete(el);
            observer.unobserve(el);
        };
    }, []);

    return (
        <Component
            ref={ref}
            style={delay > 0 && !isVisible ? { transitionDelay: `${delay}ms` } : delay > 0 ? { transitionDelay: `${delay}ms` } : undefined}
            className={`scroll-reveal ${isVisible ? 'scroll-reveal-visible' : ''} ${className}`}
        >
            {children}
        </Component>
    );
};

export default React.memo(RevealOnScroll);
